import { qbCents } from "./job-pnl";

/**
 * Expenses entered in QuickBooks (bills, checks, card charges, cash expenses, vendor credits) that
 * have a job on any line, brought in as read-only receipts on those jobs. Reads only.
 */

export type QbExpenseType = "Bill" | "Purchase" | "VendorCredit";

interface Ref {
  value?: string;
  name?: string;
}
export interface QbExpenseRaw {
  Id: string;
  SyncToken?: string;
  TxnDate?: string;
  DueDate?: string;
  DocNumber?: string;
  PrivateNote?: string;
  TotalAmt?: number | string;
  /** A bill's unpaid balance. */
  Balance?: number | string;
  VendorRef?: Ref;
  /** A Purchase's payee. */
  EntityRef?: Ref & { type?: string };
  /** A Purchase's bank or card account. */
  AccountRef?: Ref;
  PaymentType?: "Cash" | "Check" | "CreditCard";
  /** A Purchase that's a refund (money back on a card). */
  Credit?: boolean;
  SalesTermRef?: Ref;
  Line?: {
    Amount?: number | string;
    Description?: string;
    DetailType?: string;
    AccountBasedExpenseLineDetail?: { AccountRef?: Ref; CustomerRef?: Ref };
    ItemBasedExpenseLineDetail?: { ItemRef?: Ref; CustomerRef?: Ref };
  }[];
}

export interface QbExpenseReadLine {
  /** The QuickBooks account (or item) it was booked to. */
  category: string;
  description: string;
  /** Negative on a credit. */
  amountCents: number;
  /** The QuickBooks customer or job (sub-customer) on the line. */
  customerQbId: string | null;
}

export interface QbExpense {
  type: QbExpenseType;
  qbId: string;
  syncToken: string;
  kind: "bill" | "expense" | "credit";
  vendorQbId: string | null;
  vendorName: string;
  date: string | null;
  dueDate: string | null;
  number: string;
  memo: string;
  terms: string;
  paymentMethod: string;
  paidFromAccountId: string | null;
  /** Always positive; a credit's lines carry the minus. */
  totalCents: number;
  lines: QbExpenseReadLine[];
  /** A bill with nothing left to pay. */
  paid: boolean;
}

const METHOD: Record<string, string> = { Cash: "Cash", Check: "Check", CreditCard: "Company card" };

export function qbExpense(raw: QbExpenseRaw, type: QbExpenseType): QbExpense {
  const credit = type === "VendorCredit" || (type === "Purchase" && raw.Credit === true);
  const sign = credit ? -1 : 1;
  const vendor = type === "Purchase" ? raw.EntityRef : raw.VendorRef;
  const lines = (raw.Line ?? []).flatMap((l): QbExpenseReadLine[] => {
    const acct = l.AccountBasedExpenseLineDetail;
    const item = l.ItemBasedExpenseLineDetail;
    if (!acct && !item) return [];
    return [
      {
        category: (acct?.AccountRef?.name ?? item?.ItemRef?.name ?? "").trim(),
        description: (l.Description ?? "").trim().slice(0, 500),
        amountCents: sign * qbCents(String(l.Amount ?? 0)),
        customerQbId: (acct?.CustomerRef ?? item?.CustomerRef)?.value ?? null,
      },
    ];
  });
  return {
    type,
    qbId: raw.Id,
    syncToken: raw.SyncToken ?? "",
    kind: credit ? "credit" : type === "Bill" ? "bill" : "expense",
    vendorQbId: vendor?.value ?? null,
    vendorName: (vendor?.name ?? "").trim(),
    date: raw.TxnDate ?? null,
    dueDate: type === "Bill" ? (raw.DueDate ?? null) : null,
    number: (raw.DocNumber ?? "").trim(),
    memo: (raw.PrivateNote ?? "").trim().slice(0, 2000),
    terms: (raw.SalesTermRef?.name ?? "").trim(),
    paymentMethod: type === "Purchase" && raw.PaymentType ? (METHOD[raw.PaymentType] ?? "") : "",
    paidFromAccountId: type === "Purchase" ? (raw.AccountRef?.value ?? null) : null,
    totalCents: Math.abs(qbCents(String(raw.TotalAmt ?? 0))),
    lines,
    paid: type === "Bill" && qbCents(String(raw.Balance ?? 0)) === 0,
  };
}

export type QbExpenseChangeKind = "add" | "update" | "move" | "to_general" | "delete";

/** One change a sync would make (or made) to the CRM's receipts. */
export interface QbExpenseChange {
  /** Stable for the same change: what Apply sends back. */
  key: string;
  kind: QbExpenseChangeKind;
  qbType: QbExpenseType;
  qbId: string;
  receiptId: string | null;
  /** Where the receipt came from: entered here, or read from QuickBooks (null: new). */
  source: "crm" | "quickbooks" | null;
  vendorName: string;
  date: string | null;
  /** A credit is negative. */
  amountCents: number;
  /** The job it's on now and the one it goes to (labels); null: none (a general expense). */
  fromJob: string | null;
  toJob: string | null;
  note: string;
}

/**
 * New or changed in QuickBooks on a linked job: QuickBooks's own customer or job tag puts it there
 * (the link was the decision), so Sync's Apply takes it without a tick. General expenses, moves,
 * and deletions wait for review.
 */
export const expenseAppliesItself = (c: Pick<QbExpenseChange, "kind" | "toJob">) => !!c.toJob && (c.kind === "add" || c.kind === "update");

/** A receipt whose expense moved to a QuickBooks customer or job that isn't linked to a job here. */
export interface QbExpenseAttention {
  qbType: QbExpenseType;
  qbId: string;
  receiptId: string;
  vendorName: string;
  date: string | null;
  amountCents: number;
  job: string | null;
  note: string;
}

export interface QbExpensePlan {
  changes: QbExpenseChange[];
  attention: QbExpenseAttention[];
  checkedAt: string;
}

export interface QbExpenseCheckResult {
  added: number;
  updated: number;
  moved: number;
  /** Moved off every job: now general expenses. */
  toGeneral: number;
  deleted: number;
  /** Deletions held for approval (too many at once in an automatic run). */
  held: number;
  attachments: number;
  /** New expenses on QuickBooks customers or jobs not linked here (not brought in). */
  notPlaced: number;
  attention: number;
}

/** "bill" for a QuickBooks Bill, and so on, in words. */
export const QB_EXPENSE_TYPE_LABELS: Record<QbExpenseType, string> = { Bill: "Bill", Purchase: "Expense", VendorCredit: "Vendor credit" };

// ── JobNimbus invoices that don't line up with QuickBooks ──

export type QbInvoiceIssueReason = "not_in_qb" | "other_job" | "amount" | "job_not_linked";

export interface QbInvoiceIssue {
  invoiceId: string;
  jobId: string;
  jobLabel: string;
  /** As JobNimbus printed it (NMT-2026-2358-1). */
  number: string;
  totalCents: number;
  reason: QbInvoiceIssueReason;
  /** The QuickBooks customer or job that invoice number is on there. */
  qbCustomerId: string | null;
  qbCustomerName: string | null;
  qbTotalCents: number | null;
}

export const QB_INVOICE_ISSUE_LABELS: Record<QbInvoiceIssueReason, string> = {
  not_in_qb: "Not in QuickBooks",
  other_job: "On a different QuickBooks job",
  amount: "Amounts differ",
  job_not_linked: "Job not linked yet",
};
